import { reportSlugs } from '@app/ccf/company-report/slugs';
import ReportContent from '@app/components/reports/components/report-content';
import ReportMetric from '@app/components/reports/components/report-metric';
import SimpleBarChart from '@shared/components/chart/simple-bar-chart';
import { truncateLabel } from '@shared/components/chart/utils';
import Text from '@shared/components/content/text';
import { ChartConfig } from '@shared/components/ui/chart';
import { capitalizeFirstLetter } from '@shared/lib/utils';
import { useCCFReportResult } from '../../ccf-report-result-context';

export default function Packaging() {
  const {
    result,
    result: { scope3 },
  } = useCCFReportResult();

  const purchasedGoodsAndServices = scope3.categories.find(
    (category) => category.slug === reportSlugs.purchasedGoodsAndServices
  );

  const packaging = purchasedGoodsAndServices?.subCategories?.find(
    (subCategory) => subCategory.slug === reportSlugs.packaging
  );

  const data = {
    packaging_emissions: packaging?.totalSubCategoryEmissionsTonnes,
    share_of_scope_emissions: packaging?.pctOfScopeEmissions,
    share_of_total_emissions: packaging?.pctOfCompanyEmissions,
    emissions_by_materials: result.packaging?.emissionsByMaterial.reduce(
      (acc, material, idx) => {
        if (idx < 10) {
          acc.push({
            material: capitalizeFirstLetter(material.material),
            emissions: material.totalEmissionsTonnes,
            fill: 'var(--chart-3)',
          });
        }
        return acc;
      },
      [] as { material: string; emissions: number; fill: string }[]
    ),
  };

  const chartConfig = {
    emissions: {
      label: 'Emissions',
      color: 'hsl(var(--chart-1))',
      unit: 'tCO2e',
    },
  } satisfies ChartConfig;
  return (
    <ReportContent>
      <>
        <Text variant="reportBody">
          This category includes all the items you purchase to package, store
          and transport your products.
          <br />
          <br />
          This includes primary food packaging, such as cups, bowls, cans,
          bottles, lids, wrappers, cartons. But it also includes secondary and
          tertiary packaging used to group, box, store and ship your products,
          such as cardboard boxes, tape, pallet wrap.
        </Text>
        <ReportMetric
          title="Category emissions"
          value={data.packaging_emissions!}
          unit="tCO2e"
          size="lg"
        />
        <div className="flex space-x-2">
          <ReportMetric
            title="Share of Scope 3 emissions"
            value={data.share_of_scope_emissions!}
            unit="%"
            size="md"
            className="flex-1"
          />
          <ReportMetric
            title="Share of total emissions"
            value={data.share_of_total_emissions!}
            unit="%"
            size="md"
            className="flex-1"
          />
        </div>
      </>
      <>
        <Text variant="h3">Emissions by material</Text>
        <SimpleBarChart
          nameKey="material"
          dataKey="emissions"
          chartData={data.emissions_by_materials}
          chartConfig={chartConfig}
          className="size-full max-h-[calc(var(--content-title-height)-15rem)]"
          layout="vertical"
          margin={{ left: 20, right: 80 }}
          XAxisProps={{
            label: {
              value: 'tCO2e',
              position: 'right',
              offset: 20,
            },
          }}
          YAxisProps={{
            tickFormatter: (value: string) => truncateLabel(value, 12),
          }}
        />
      </>
    </ReportContent>
  );
}
